import Http from "../utilities/Http";
import {API_ROOT} from "../config";

// Grammar errors are shown in the review modal. Avoid logging Axios request
// configuration, which can include the unsent draft.
const inboxAiRequest = (cancelToken, timeout = 30000) => ({cancelToken, timeout, suppressErrorAlert: true, suppressErrorLogging: true});

export default {
    async getInboxAiAvailability(organizationId, cancelToken = null) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/inbox-ai`, inboxAiRequest(cancelToken));
        return response.data;
    },
    async checkInboxAiGrammar(organizationId, input, cancelToken = null) {
        const response = await Http().post(
            `${API_ROOT}/organizations/${organizationId}/inbox-ai/check`,
            input,
            inboxAiRequest(cancelToken, 100000)
        );
        return response.data.result;
    },
    async getMessageFeeds(organizationId, filters = {}, page = 0, perPage = 20, cancelToken = null, updatedSince = null) {
        const params = {
            perPage,
            page,
            ...filters
        };
        
        if (updatedSince) {
            params.updatedSince = updatedSince;
        }
        
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds`, {
            params,
            cancelToken
        });

        return response.data;
    },
    async insertMessageFeed(organizationId, messageType, destination, subject = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/`, {
            messageType,
            destination,
            subject,
        });

        return response.data.messageFeed;
    },
    async getMessageFeed(organizationId, messageFeedId, cancelToken = null, params = {}) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}`, {
            params,
            cancelToken
        });

        return response.data;
    },
    async getMessageFeedMessages(organizationId, messageFeedId, page = 0, perPage = 10, cancelToken = null, oldestMessageTime = null, mostRecentTime = null) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}`, {
            params: {
                page,
                perPage,
                messages: true,
                oldestMessageTime,
                mostRecentTime
            },
            cancelToken
        });

        return response.data;
    },
    async resolveMessageFeed(organizationId, messageFeedId, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}`, {
            resolve: true
        },
            {cancelToken}
        );

        return response.data;
    },
    async resolveAllMessageFeeds(organizationId, userOrganization) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/resolve-all`, {
            userOrganization,
        });

        return response.data;
    },
    async getUnresolvedFeedsByUser(organizationId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/unresolved-by-user`);

        return response.data;
    },
    async getInboxReport(organizationId, search = null, startDate = null, endDate = null, perPage = null, currentPage = null, ordering = null) {
        const params = {
            search,
            startDate,
            endDate,
            perPage,
            currentPage,
        };

        if (ordering && ordering.length > 0) {
            params.ordering = JSON.stringify(ordering);
        }

        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/inbox-report`, {
            params
        });

        return response.data;
    },
    async bulkReassignFeeds(organizationId, userOrganization, messageFeedIds, targetType, targetId) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/bulk-reassign`, {
            userOrganization,
            messageFeedIds,
            targetType,
            targetId,
        });

        return response.data;
    },
    async updateMessageFeed(organizationId, messageFeedId, messageFeed, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}?messages=true`, {
                messageFeed
            },
            {cancelToken}
        );

        return response.data;
    },
    async updateMessageFeedShowHtmlImages(organizationId, messageFeedId, showImages = true) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}`, {
            showHtmlImages: showImages ? 1 : 0
        });

        return response.data;
    },
    async sendNewMessage(organizationId, messageFeedId, message, attachments = [], subject = null) {
        const data = {
            message,
            attachments
        };

        if (subject) {
            data.subject = subject;
        }

        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}?messages=true`, data);

        return response.data;
    },
    async validatePhoneNumber(phoneNumber, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/twilio/verify-phone-number`, {
                phoneNumber
            },
            {cancelToken}
        );

        return response.data.valid;
    }, async getMessageFeedJobs(organizationId, messageFeedId, page = 0, perPage = 20, cancelToken = null) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/get-message-feed-jobs/${messageFeedId}`, {
            params: {
                perPage,
                page,
            },
            cancelToken
        });

        return response.data.jobs;
    },
    async toggleMessageFeedBlocked(organizationId, messageFeedId) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}?messages=true`, {
            toggleBlocked: true
        });

        return response.data;
    },
    async addMessageFeedNote(organizationId, messageFeedId, note) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/${messageFeedId}?messages=true`, {
            note
        });

        return response.data;
    },
    async uploadImage(organizationId, formData) {
        let response = await Http().post(API_ROOT + `/organizations/${organizationId}/message-feeds/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });

        return response.data.fileName;
    },
    async getAutoReplies(organizationId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/auto-replies`);

        return response.data.autoReplies;
    },
    async insertAutoReply(organizationId, autoReply) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/auto-replies`, {
            autoReply
        });

        return response.data.autoReply;
    },
    async updateAutoReply(organizationId, autoReplyId, autoReply, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/auto-replies/${autoReplyId}`, {
                autoReply
            },
            {cancelToken}
        );

        return response.data.autoReply;
    },
    async deleteAutoReply(organizationId, autoReplyId) {
        await Http().delete(`${API_ROOT}/organizations/${organizationId}/auto-replies/${autoReplyId}`);
    },
    async getMessageScripts(organizationId, params) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-scripts`, {
            params
        });

        return response.data.messageScripts;
    },
    async getMessageScript(organizationId, messageScriptId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-scripts/${messageScriptId}`);

        return response.data.messageScript;
    },
    async insertMessageScript(organizationId, messageScript) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-scripts`, {
                messageScript
            }
        );

        return response.data.messageScript;
    },
    async updateMessageScript(organizationId, messageScriptId, messageScript, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-scripts/${messageScriptId}`, {
                messageScript
            },
            {cancelToken}
        );

        return response.data.messageScript;
    },
    async deleteMessageScript(organizationId, messageScriptId) {
        const response = await Http().delete(`${API_ROOT}/organizations/${organizationId}/message-scripts/${messageScriptId}`, {});
    },
    async getScriptVariables(organizationId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-script-variables`);

        return response.data.messageScriptVariables;
    },
    async getScriptVariable(organizationId, messageScriptVariableId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-script-variables/${messageScriptVariableId}`);

        return response.data.messageScriptVariable;
    },
    async insertScriptVariable(organizationId, messageScriptVariable) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-script-variables`, {
                messageScriptVariable
            }
        );

        return response.data.messageScriptVariable;
    },
    async updateScriptVariable(organizationId, messageScriptVariableId, messageScriptVariable) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-script-variables/${messageScriptVariableId}`, {
                messageScriptVariable
            }
        );

        return response.data.messageScriptVariable;
    },
    async deleteScriptVariable(organizationId, messageScriptVariableId) {
        const response = await Http().delete(`${API_ROOT}/organizations/${organizationId}/message-script-variables/${messageScriptVariableId}`, {});
    },
    async getMessageScriptVariableTypes() {
        const response = await Http().get(`${API_ROOT}/message-script-variable-types`);

        return response.data.variableTypes;
    },
    async getScriptCategories(organizationId) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-script-categories`);

        return response.data.messageScriptCategories;
    },
    async getScriptOutput(organizationId, messageScriptId, params, cancelToken = null) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-scripts/get-message-script-output/${messageScriptId}`,
            {
                params,
                cancelToken
            }
        );

        return response.data.messageScriptOutput;
    },
    async setUserTyping(organizationId, messageFeedId, typing, cancelToken = null) {
        const response = await Http().post(`${API_ROOT}/organizations/${organizationId}/message-feeds/set-feed-typing/${messageFeedId}`, {
                typing
            },
            {cancelToken}
        );

        return response.data;
    },
    async getMessageFeedUserCounts(organizationId, filters = {}) {
        const response = await Http().get(API_ROOT + '/organizations/' + organizationId + '/message-feeds/user-message-counts', {
            params: filters,
        });
        return response.data;
    },
    async getMessageFeedsTotals(organizationId, cancelToken = null) {
        const response = await Http().get(`${API_ROOT}/organizations/${organizationId}/message-feeds/totals`, {
            cancelToken
        });

        return response.data.totalUnresolved;
    },

    async getAutomationConfig(organizationId) {
        let response = await Http().get(API_ROOT + '/organizations/' + organizationId +'/message-automations/config');
        return response.data;
    },
    async getAutomations(organizationId, status = null) {
        const config = {}
        if (status) {
            config.params = {
                status
            }
        }
        let response = await Http().get(API_ROOT + '/organizations/' + organizationId +'/message-automations', config);
        return response.data;
    },
    async getAutomation(organizationId, automationId) {
        let response = await Http().get(API_ROOT + '/organizations/' + organizationId +'/message-automations/' + automationId);
        return response.data;
    },
    async insertAutomation(organizationId, automation) {
        let response = await Http().post(API_ROOT + '/organizations/' + organizationId +'/message-automations', {
            automation
        });
        return response.data;
    },
    async updateAutomation(organizationId, automationId, automation) {
        let response = await Http().post(API_ROOT + '/organizations/' + organizationId +'/message-automations/' + automationId, {
            automation
        });
        return response.data;
    },
    async deleteAutomation(organizationId, automationId) {
        let response = await Http().delete(API_ROOT + '/organizations/' + organizationId +'/message-automations/' + automationId);
        return response.data;
    },
    async updateAutomationActiveStatus(organizationId, automationId, active) {
        let response = await Http().post(API_ROOT + '/organizations/' + organizationId +'/message-automations/' + automationId, {
            status: active,
        });
        return response.data;
    },
    async insertMessageSetting(organizationId, key, value) {
        let response = await Http().post(API_ROOT + '/organizations/' + organizationId +'/message-settings', {
            key,
            value
        });
        return response.data;
    },
    async getMessageSetting(organizationId, key) {
        let response = await Http().get(API_ROOT + '/organizations/' + organizationId +'/message-settings/' + key);
        return response.data;
    },
    async deleteMessageSetting(organizationId, id) {
        let response = await Http().delete(API_ROOT + '/organizations/' + organizationId +'/message-settings/' + id);
        return response.data;
    },
}
